import { useCallback, useEffect, useState } from "react";
import { IRegion } from "../shared/types";
import {
  REGION_DURATION,
  DASHBOARD_ONPREM_SORT_OPTIONS as SORT_OPTIONS,
  DEFAULT_ONPREM_CLASSIFICATION_SORT as DEFAULT_SORT,
} from "../shared/constants";
import useDashboardService from "../services";
import { toast } from "react-toastify";
import { ListCard } from "../shared";
import { useNavigate } from "react-router-dom";
import Region from "./region";
import { useTheme } from "@mui/material/styles";
import { Box, Paper, Skeleton, Typography, Button } from "@mui/material";
import eventEmitter from "../../../components/utils/eventEmitter";
import useFrameworks from "../../../shared/hooks/useFrameworks";
// import styles from "../../dashboard/shared/shared.module.scss";


// Note: avoid injecting global scrollbar styles here — keep any scrolling
// changes scoped to this component only.

// complianceKey: default framework score key (CIS_8 unless CIS is switched off)
const fetchSortDetails = (sortBy: string, complianceKey: string) => {
  // Extract the base field and order
  const [baseField, order] = sortBy.split("_");
  
  // Map the field to the actual compliance field name if it's a compliance sort
  let field = baseField;
  if (baseField === "compliance") {
    // Use the selected compliance column if available
    field = complianceKey;
  } else if (baseField === "cve") {
    field = "cve_score";
  } else if (baseField === "vulnerabilities") {
    field = "vulnerability";
  } else if (baseField === "assets") {
    field = "devices";
  }
  
  console.log('Sort By:', sortBy);
  console.log('Base Field:', baseField);
  console.log('Mapped Field:', field);
  return { field, order };
};

interface StackItem {
  region: IRegion;
  level: number;
}

/* removed global hide-scrollbar styles to avoid affecting other components */

const containerStyles = {
  borderRadius: "4px",
  position: "relative",
  height: "100%",
  "& .MuiPaper-root": {
    // borderRadius: "inherit",
    overflow: "hidden",
    height: "100%"
  }
};

// Scoped sx overrides applied only to this component's root Box.
// Target ListCard DOM structure using child selectors so it works with CSS modules.
const localScrollOverrides = {
  height: "100%",
  display: "flex",
  flexDirection: "column",
  // ListCard renders: <div data-theme> <div class={container}> <div class={header}/> <div class={data_container}> ...
  // Target the ListCard wrapper (first child) and its inner container (first div inside it).
  "& > *": {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  // The inner container div (inside data-theme) => ensure it's column and full height
  "& > * > div": {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  // Header is the first child inside that container: keep it from shrinking
  "& > * > div > div:first-of-type": {
    flex: "0 0 auto",
  },
  // The data container is the second child inside container: make it take remaining space and scroll
  "& > * > div > div:nth-of-type(2)": {
    flex: "1 1 auto",
    minHeight: 0,
    overflowY: "auto",
  },
  // The immediate inner padding wrapper (first child inside data container) may have inline fixed heights
  "& > * > div > div:nth-of-type(2) > div": {
    height: "auto !important",
    maxHeight: "none !important",
    overflowY: "auto !important",
  },
};

const OnPremClassification = () => {
  const navigate = useNavigate();
  // NOTE: fetchOnPremRegionWiseData calls END_POINTS.DASHBOARD_SCORE with payload { module: "dashboard-regionwise", ...payload }
  // (Migrated from deprecated standalone endpoint 'securityscan/dashboard-regionwise')
  const { fetchOnPremRegionWiseData } = useDashboardService();
  const { isFrameworkKeyOn, defaultFrameworkKey } = useFrameworks();
  const [regionsData, setRegionsData] = useState<IRegion[]>([]);  
  const [sortBy, setSortBy] = useState(DEFAULT_SORT);
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const [trigger, setTrigger] = useState(0);
  const [nistColumns, setNistColumns] = useState<string[]>([]);
  const[pcidssColumns, setpcidssColumns] = useState<string[]>([]);
  const [cisColumns, setCisColumns] = useState<string[]>([]);
  const [selectedItem, setSelectedItem] = useState<string>("");
  const [selectedRegion, setSelectedRegion] = useState<IRegion | null>(null);
  const [subRegions, setSubRegions] = useState<IRegion[]>([]);
  //  const [breadcrumbLevels, setBreadcrumbLevels] = useState<IRegion[]>([]);
  const [regionStack, setRegionStack] = useState<StackItem[]>([]);
  const [displayBreadcrums, setDisplayBreadcrums] = useState<StackItem[]>([]);
    const onLevelSelect = (levelIndex:any) => {
      const newLevels = regionStack.slice(0, levelIndex + 1);
      setRegionStack(newLevels);
      const selectedLevel = newLevels[newLevels.length - 1];
      // onRowClick(selectedLevel);
    };
    
    const onBack = async () => {
      if (regionStack.length > 1) {
        setLoading(true);
        const newStack = [...regionStack];
        newStack.pop();
        const previousLevel = newStack[newStack.length - 1];
        setRegionStack(newStack); 
        const displayBreadcrums = [...newStack];
        if (displayBreadcrums.length > 0) {
          displayBreadcrums.pop();
          setDisplayBreadcrums(displayBreadcrums);
        }
        await onRowClick(previousLevel.region);
      } else if (regionStack.length === 1) {
        setLoading(true);
        setTimeout(() => {
          setSelectedRegion(null);
          setRegionStack([]);
          setLoading(false);
        }, 100);
      }
    };

  useEffect(() => {
    const handleFilterSaved = () => {
      setLoading(true);
      setTrigger((prev) => prev + 1);
    };

    eventEmitter.on("filterSaved", handleFilterSaved);

    return () => {
      eventEmitter.off("filterSaved", handleFilterSaved);
    };
  }, []);

  useEffect(() => {
    fetchRegionData();
  }, [sortBy, trigger]);

  // Columns only for frameworks switched on for the organization
  const generateNISTColumns = (data: any[]) => {
    if (!data || data.length === 0) return [];
    const nistKeys = Object.keys(data[0]).filter((key) =>
      key.startsWith("NIST_") && isFrameworkKeyOn(key)
    );
    return nistKeys;
  };
    const generatePCIDSSColumns = (data: any[]) => {
    if (!data || data.length === 0) return [];
    const pciKeys = Object.keys(data[0]).filter((key) =>
      key.startsWith("PCI ") && isFrameworkKeyOn(key)
    );
    return pciKeys;
  };

  const generateCisColumns = (data: any[]) => {
    if (!data || data.length === 0) return [];
    const cisKeys = Object.keys(data[0]).filter((key) =>
      key.startsWith("CIS_") && isFrameworkKeyOn(key)
    );
    return cisKeys;
  };

  const handleColumnSelection = (selectedColumn: string) => {
    setLoading(true);
    setSelectedItem(selectedColumn);
    setTimeout(() => {
      setLoading(false);
    }, 500);
  };

  const fetchRegionData = async () => {
    setLoading(true);
    const { field, order } = fetchSortDetails(sortBy, defaultFrameworkKey);
    try {
      const payload = {
        duration: REGION_DURATION,
        sort: { field, order },
        level: 0,
      };
      const { data, error }: any = await fetchOnPremRegionWiseData(payload);
      if (data) {
        const slicedData = data.slice(0, 20);
        setRegionsData(slicedData);
        const nistCols = generateNISTColumns(slicedData);
        const cisCols = generateCisColumns(slicedData);
        const pcidssCols = generatePCIDSSColumns(slicedData);
        setpcidssColumns(pcidssCols);
        setNistColumns(nistCols);
        setCisColumns(cisCols);

        // Set initial column definitions with first NIST and CIS columns
        if (nistCols.length > 0 && cisCols.length > 0) {
          handleColumnSelection(nistCols[0]);
        }
      }
      if (error) toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const onRowClick = async (data: IRegion) => {
    if (!data.leaf_node) {
      setLoading(true);
      const { field, order } = fetchSortDetails(sortBy, defaultFrameworkKey);
      const payload = {
        duration: REGION_DURATION,
        sort: { field, order },
        level: data.level,
        id: data.id,
      };

      try {
        const { data: responseData, error } = await fetchOnPremRegionWiseData(
          payload
        );
        if (error) {
          toast.error("Failed to fetch region data");
        } else if (responseData) {
          const existingIndex = regionStack.findIndex(
            item => item.region.id === data.id
          );
          
          setRegionStack(prev => {
            let newStack;
            if (existingIndex === -1) {
              // Add new region if it doesn't exist
               newStack = [...prev, { region: data, level: data.level }];
            } else {
               newStack = [...prev.slice(0, existingIndex), { region: data, level: data.level }];
            }
              const breadcrums = [...newStack];
            if (breadcrums.length > 0) {
              breadcrums.pop();
            }
            setDisplayBreadcrums(breadcrums);
            return newStack;
          });
          setSelectedRegion(data);
          setSubRegions(responseData);
          setLoading(false);
        }
      } catch (error) {
        console.error("Error fetching region data:", error);
        toast.error("Failed to fetch region data");
      }
    } else {
      return;
    }
  };

  const renderRegionItem = useCallback(
    (item: IRegion, index: number) => {
      const { field } = fetchSortDetails(sortBy, defaultFrameworkKey);
      // Use the selected column from the column selector if available
      const selectedCompliance = selectedItem || field;
      console.log('Rendering Region with compliance:', selectedCompliance);
      return <Region key={index} data={item} onClick={onRowClick} selectedCompliance={selectedCompliance} />;
    },
    [sortBy, selectedItem, defaultFrameworkKey]
  );

  // Function to handle header click
  const onHeaderClick = async () => {
    if (!selectedRegion || selectedRegion.leaf_node) return;
    const { field, order } = fetchSortDetails(sortBy, defaultFrameworkKey);
    const payload = {
      duration: REGION_DURATION,
      sort: { field, order },
      level: selectedRegion.level,
      id: selectedRegion.id,
    };
    try {
      const { data: responseData, error } = await fetchOnPremRegionWiseData(
        payload
      );
      if (error) {
        toast.error("Failed to fetch region data");
      } else if (responseData) {
        setSelectedRegion(responseData[0]);
      }
    } catch (error) {
      console.error("Error fetching region data:", error);
      toast.error("Failed to fetch region data");
    }
  };



  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: "4px",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          bgcolor: theme.palette.mode === "dark" ? "background.paper" : "white",
          // boxShadow: theme.palette.mode === "dark"
          //   ? "0px 4px 12px rgba(0, 0, 0, 0.3)"
          //   : "0px 4px 12px rgba(0, 0, 0, 0.1)",
          position: "relative",
          overflow: "hidden"
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1.5,
          }}
        >
          <Skeleton variant="text" width={200} height={32} />
          <Box sx={{ display: "flex", gap: 1 }}>
            <Skeleton
              variant="rectangular"
              width={120}
              height={36}
              sx={{ borderRadius: 1 }}
            />
            <Skeleton
              variant="rectangular"
              width={120}
              height={36}
              sx={{ borderRadius: 1 }}
            />
          </Box>
        </Box>

        {/* List items */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 ,}}>
          {[...Array(3)].map((_, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 1.5,
                borderRadius: 1,
                bgcolor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.05)"
                    : "rgba(0, 0, 0, 0.02)",
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}
              >
                <Skeleton variant="circular" width={32} height={32} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="60%" height={20} />
                  <Skeleton
                    variant="text"
                    width="40%"
                    height={14}
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              </Box>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Skeleton variant="text" width={60} height={20} />
                <Skeleton variant="text" width={60} height={20} />
              </Box>
            </Box>
          ))}
        </Box>
      </Paper>
    );
  }

  return (
    <Box
      data-theme={theme.palette.mode}
      sx={{
        ...containerStyles,
        ...localScrollOverrides,
        background: theme.palette.mode === "dark" ? "background.paper" : "white"
      }}
    >
      {selectedRegion ? (
        <ListCard<IRegion>
          title={"On-prem Classification"}
          sortOptions={SORT_OPTIONS}
          sortBy={sortBy}
          setSortBy={setSortBy}
          showFilter={true}
          loading={loading}
          data={subRegions}
          showBreadCrums={true}
          selectedRegion={selectedRegion}
          columns={[...nistColumns, ...cisColumns, ...pcidssColumns]}
          onColumnSelection={handleColumnSelection}
          renderItem={renderRegionItem}
          onBack={onBack}
          onLevelSelect={onLevelSelect}
          breadcrumbLevels={displayBreadcrums}
          children={
            !loading && (!subRegions || subRegions.length === 0) ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "200px",
                  color: theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.7)"
                    : "rgba(0, 0, 0, 0.6)",
                }}
              >
                <Typography variant="body2">No Rows To Show</Typography>
              </Box>
            ) : undefined
          }
        />
      ) : (
        <ListCard<IRegion>
          title={"On-prem Classification"}
          sortOptions={SORT_OPTIONS}
          sortBy={sortBy}
          showSelectColumns={true}
          setSortBy={setSortBy}
          showFilter={true}
          loading={loading}
          data={regionsData}
          columns={[...nistColumns, ...cisColumns, ...pcidssColumns]}
          onColumnSelection={handleColumnSelection}
          renderItem={renderRegionItem}
          children={
            !loading && (!regionsData || regionsData.length === 0) ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "200px",
                  color: theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.7)"
                    : "rgba(0, 0, 0, 0.6)",
                }}
              >
                <Typography variant="body2">No Rows To Show</Typography>
              </Box>
            ) : undefined
          }
        />
      )}
    </Box>
  );
};

export default OnPremClassification;
